// src/util/conversionTracking.js

/**
 * Configuration for conversion tracking pixels
 * Updated with correct values from Google Ads admin screenshots
 */
export const CONVERSION_IDS = {
  // Google Ads - Updated with values from event snippet
  GOOGLE_ADS: {
    CONVERSION_ID: 'AW-16991673413',
    CONVERSION_LABEL: 'iNMOCKXI3rkaEMW4oaY_',
    ENABLED: true,
  },
  
  // Facebook Pixel
  FACEBOOK: {
    PIXEL_ID: '', // Replace with your Facebook Pixel ID when you have it
    ENABLED: false, // Set to true when you have a valid Facebook Pixel ID
  },
  
  // Microsoft Bing Ads
  MICROSOFT_ADS: {
    UET_TAG_ID: '343188309',
    ENABLED: true,
  }
};

/**
 * Check if Google Ads tracking is enabled with valid IDs
 * @returns {boolean}
 */
export const isGoogleAdsEnabled = () => {
  const { CONVERSION_ID, CONVERSION_LABEL, ENABLED } = CONVERSION_IDS.GOOGLE_ADS;
  return ENABLED && CONVERSION_ID && CONVERSION_LABEL;
};

/**
 * Check if Facebook Pixel tracking is enabled with valid ID
 * @returns {boolean}
 */
export const isFacebookEnabled = () => {
  const { PIXEL_ID, ENABLED } = CONVERSION_IDS.FACEBOOK;
  return ENABLED && PIXEL_ID && PIXEL_ID.trim() !== '';
}; 

/**
 * Generate Google Ads conversion tracking script
 * @param {Object} sessionDetails - Payment session details
 * @returns {string} Script content
 */
export const getGoogleAdsScript = (sessionDetails) => {
  if (!isGoogleAdsEnabled()) return '';
  
  const { CONVERSION_ID, CONVERSION_LABEL } = CONVERSION_IDS.GOOGLE_ADS;
  const value = sessionDetails?.amount_total ? sessionDetails.amount_total / 100 : 0;
  const sendTo = `${CONVERSION_ID}/${CONVERSION_LABEL}`;
  
  return `
    gtag('event', 'conversion', {
      'send_to': '${sendTo}',
      'value': ${value},
      'currency': '${sessionDetails?.currency?.toUpperCase() || 'USD'}',
      'transaction_id': '${sessionDetails?.id || ''}',
    });
    console.log('Google Ads conversion tracked:', {
      'conversionId': '${CONVERSION_ID}',
      'conversionLabel': '${CONVERSION_LABEL}',
      'value': ${value},
      'currency': '${sessionDetails?.currency?.toUpperCase() || 'USD'}',
      'transactionId': '${sessionDetails?.id || ''}'
    });
  `;
};

/**
 * Check if Microsoft Ads tracking is enabled
 * @returns {boolean}
 */
export const isMicrosoftAdsEnabled = () => {
  const { UET_TAG_ID, ENABLED } = CONVERSION_IDS.MICROSOFT_ADS;
  return ENABLED && UET_TAG_ID && UET_TAG_ID.trim() !== '';
};

/**
 * Generate Microsoft Ads conversion tracking script
 * @param {Object} sessionDetails - Payment session details
 * @returns {string} Script content
 */
export const getMicrosoftAdsScript = (sessionDetails) => {
  if (!isMicrosoftAdsEnabled()) return '';

  const value = sessionDetails?.amount_total ? sessionDetails.amount_total / 100 : 0;

  return `
    window.uetq = window.uetq || [];
    window.uetq.push({ 
      'ec': 'purchase',
      'ea': 'conversion',
      'el': '${sessionDetails.metadata?.planType || 'subscription'}',
      'ev': ${value},
      'gv': ${value},
      'gc': '${sessionDetails?.currency?.toUpperCase() || 'USD'}',
      'orderId': '${sessionDetails?.id || ''}'
    });
    console.log('Microsoft Ads conversion tracked');
  `;
};

/**
 * Generate Facebook Pixel conversion tracking script
 * @param {Object} sessionDetails - Payment session details
 * @returns {string} Script content
 */
export const getFacebookPixelScript = (sessionDetails) => {
  if (!isFacebookEnabled()) return '';
  
  const { PIXEL_ID } = CONVERSION_IDS.FACEBOOK;
  const value = sessionDetails?.amount_total ? sessionDetails.amount_total / 100 : 0;
  
  return `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${PIXEL_ID}');
    fbq('track', 'Purchase', {
      value: ${value},
      currency: '${sessionDetails?.currency?.toUpperCase() || 'USD'}',
      content_type: 'product',
      content_ids: ['${sessionDetails?.metadata?.planType || 'subscription'}']
    });
    console.log('Facebook Pixel conversion tracked');
  `;
};

/**
 * Get Facebook Pixel noscript tag content for fallback
 * @returns {string} Noscript content
 */
export const getFacebookNoscriptContent = () => {
  if (!isFacebookEnabled()) return '';
  
  const { PIXEL_ID } = CONVERSION_IDS.FACEBOOK;
  
  return `
    <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${PIXEL_ID}&ev=Purchase&noscript=1"
    />
  `;
};